import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const CART_NETWORK_UPDATE_EVENT = 'cart:network-updated';
const CART_MUTATION_PATH_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;

/**
 * @param {unknown} input
 * @returns {string}
 */
function normalizeRequestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return '';
}

/**
 * @param {string} url
 * @returns {boolean}
 */
function isCartMutationUrl(url) {
  if (!url) return false;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return CART_MUTATION_PATH_PATTERN.test(parsedUrl.pathname + parsedUrl.search);
  } catch (_) {
    return CART_MUTATION_PATH_PATTERN.test(url);
  }
}

/**
 * Installs global listeners for cart mutation network requests.
 */
function installCartNetworkSync() {
  const globalWindow = /** @type {any} */ (window);
  if (globalWindow.__cartNetworkSyncInstalled) return;

  globalWindow.__cartNetworkSyncInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    if (response.ok && isCartMutationUrl(normalizeRequestUrl(args[0]))) {
      document.dispatchEvent(new CustomEvent(CART_NETWORK_UPDATE_EVENT));
    }

    return response;
  };

  const xhrPrototype = /** @type {any} */ (XMLHttpRequest.prototype);
  const originalOpen = xhrPrototype.open;
  const originalSend = xhrPrototype.send;
  const xhrMutationUrlMap = new WeakMap();

  xhrPrototype.open = function () {
    const url = arguments[1];
    xhrMutationUrlMap.set(this, normalizeRequestUrl(url));
    return originalOpen.apply(this, arguments);
  };

  xhrPrototype.send = function () {
    this.addEventListener(
      'load',
      () => {
        if (this.status >= 200 && this.status < 300 && isCartMutationUrl(xhrMutationUrlMap.get(this) ?? '')) {
          document.dispatchEvent(new CustomEvent(CART_NETWORK_UPDATE_EVENT));
        }
      },
      { once: true }
    );

    return originalSend.apply(this, arguments);
  };
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    installCartNetworkSync();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(CART_NETWORK_UPDATE_EVENT, this.onCartNetworkUpdated);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(CART_NETWORK_UPDATE_EVENT, this.onCartNetworkUpdated);
  }

  /**
   * Handles cart updates triggered by network cart mutations.
   */
  onCartNetworkUpdated = async () => {
    const fetchedCount = await this.fetchCartItemCount();

    if (fetchedCount != null) {
      this.renderCartBubble(fetchedCount, false, false);
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const sectionItemCount = this.getItemCountFromSections(event.detail.data?.sections);
    const source = event.detail.data?.source;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    if (sectionItemCount != null) {
      this.renderCartBubble(sectionItemCount, false);
      return;
    }

    if (comingFromProductForm || source === 'cart-items-component' || source === 'cart-drawer-component') {
      const fetchedCount = await this.fetchCartItemCount();

      if (fetchedCount != null) {
        this.renderCartBubble(fetchedCount, false);
        return;
      }
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Gets the total cart count from /cart.js.
   * @returns {Promise<number | null>} The cart item count, or null if request fails.
   */
  async fetchCartItemCount() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return null;

      const cart = await response.json();
      const count = Number(cart?.item_count);

      if (Number.isNaN(count) || count < 0) return null;

      return count;
    } catch (_) {
      return null;
    }
  }

  /**
   * Gets the latest cart item count from section rendering HTML, if available.
   * @param {Record<string, string> | undefined} sections - Section Rendering API response.
   * @returns {number | null} The parsed cart item count, or null if unavailable.
   */
  getItemCountFromSections(sections) {
    if (!sections) return null;

    for (const sectionHtml of Object.values(sections)) {
      if (typeof sectionHtml !== 'string' || sectionHtml.trim() === '') continue;

      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');

      const countElement = doc.querySelector('[data-testid="cart-icon"] [ref="cartBubbleCount"]');
      const rawCount = countElement?.textContent?.trim() ?? '';

      if (rawCount !== '') {
        const count = parseInt(rawCount, 10);

        if (!Number.isNaN(count)) return count;
      }

      const accessibilityText = doc
        .querySelector('[data-testid="cart-icon"] .visually-hidden')
        ?.textContent?.trim();

      if (accessibilityText) {
        const matchedCount = accessibilityText.match(/(\d+)/);

        if (matchedCount?.[1]) {
          const count = parseInt(matchedCount[1], 10);

          if (!Number.isNaN(count)) return count;
        }
      }
    }

    return null;
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    const safeItemCount = Number.isFinite(itemCount) ? itemCount : 0;
    const nextCount = comingFromProductForm ? this.currentCartCount + safeItemCount : safeItemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', nextCount > 0 && animate);

    this.currentCartCount = nextCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
